import { supabase } from '../lib/supabase';

/**
 * Sprawdza status relacji znajomych między dwoma użytkownikami
 * @param {string} userId1 - ID pierwszego użytkownika
 * @param {string} userId2 - ID drugiego użytkownika
 * @returns {Promise<string>} - Status: 'none', 'pending_sent', 'pending_received', 'accepted', 'rejected', 'cancelled'
 */
export async function getFriendStatus(userId1, userId2) {
  try {
    // Sprawdź oba kierunki zaproszenia
    const { data, error } = await supabase
      .from('friend_invitations')
      .select('*')
      .or(`sender_id.eq.${userId1},sender_id.eq.${userId2},receiver_id.eq.${userId1},receiver_id.eq.${userId2}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error checking friend status:', error);
      return 'none';
    }

    if (!data || data.length === 0) {
      return 'none';
    }

    // Znajdź zaproszenie między tymi dwoma użytkownikami
    const invitation = data.find(
      inv => 
        (inv.sender_id === userId1 && inv.receiver_id === userId2) ||
        (inv.sender_id === userId2 && inv.receiver_id === userId1)
    );

    if (!invitation) {
      return 'none';
    }

    // Sprawdź kierunek zaproszenia z perspektywy userId1
    if (invitation.sender_id === userId1 && invitation.receiver_id === userId2) {
      // userId1 sent invitation
      if (invitation.status === 'pending') return 'pending_sent';
      if (invitation.status === 'accepted') return 'accepted';
      if (invitation.status === 'rejected') return 'rejected';
      if (invitation.status === 'cancelled') return 'cancelled';
    } else if (invitation.sender_id === userId2 && invitation.receiver_id === userId1) {
      // userId1 received invitation
      if (invitation.status === 'pending') return 'pending_received';
      if (invitation.status === 'accepted') return 'accepted';
      if (invitation.status === 'rejected') return 'rejected';
      if (invitation.status === 'cancelled') return 'cancelled';
    }

    return invitation.status || 'none';
  } catch (error) {
    console.error('Error checking friend status:', error);
    return 'none';
  }
}

/**
 * Wysyła zaproszenie do znajomych
 * @param {string} receiverId - ID użytkownika, któremu wysyłamy zaproszenie
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function sendFriendInvitation(receiverId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    if (user.id === receiverId) {
      return { success: false, error: 'Cannot send invitation to yourself' };
    }

    // Sprawdź czy istnieje zaproszenie w kierunku user -> receiver
    const { data: existingInvitation1, error: checkError1 } = await supabase
      .from('friend_invitations')
      .select('*')
      .eq('sender_id', user.id)
      .eq('receiver_id', receiverId)
      .maybeSingle();

    if (checkError1) {
      console.error('Error checking existing invitation:', checkError1);
    }

    // Sprawdź też w drugim kierunku (receiver -> user)
    const { data: existingInvitation2, error: checkError2 } = await supabase
      .from('friend_invitations')
      .select('*')
      .eq('sender_id', receiverId)
      .eq('receiver_id', user.id)
      .maybeSingle();

    if (checkError2) {
      console.error('Error checking existing invitation (reverse):', checkError2);
    }

    // Jeśli istnieje zaproszenie w kierunku user -> receiver
    if (existingInvitation1) {
      if (existingInvitation1.status === 'pending') {
        return { success: false, error: 'Invitation already sent' };
      }
      if (existingInvitation1.status === 'accepted') {
        return { success: false, error: 'You are already friends' };
      }
      // Jeśli status to 'rejected' lub 'cancelled', usuń stare zaproszenie przed utworzeniem nowego
      if (existingInvitation1.status === 'rejected' || existingInvitation1.status === 'cancelled') {
        const { error: deleteError } = await supabase
          .from('friend_invitations')
          .delete()
          .eq('id', existingInvitation1.id);

        if (deleteError) {
          console.error('Error deleting old invitation:', deleteError);
          return { success: false, error: 'Error deleting old invitation: ' + deleteError.message };
        }
      }
    }

    // Jeśli istnieje zaproszenie w kierunku receiver -> user (odwrotnym)
    if (existingInvitation2) {
      if (existingInvitation2.status === 'pending') {
        // Otrzymałeś zaproszenie od tego użytkownika - nie możesz wysłać swojego, ale możesz je zaakceptować
        return { success: false, error: 'You have a pending invitation from this user' };
      }
      if (existingInvitation2.status === 'accepted') {
        return { success: false, error: 'You are already friends' };
      }
      // Jeśli status to 'rejected' lub 'cancelled', usuń stare zaproszenie przed utworzeniem nowego
      if (existingInvitation2.status === 'rejected' || existingInvitation2.status === 'cancelled') {
        const { error: deleteError } = await supabase
          .from('friend_invitations')
          .delete()
          .eq('id', existingInvitation2.id);

        if (deleteError) {
          console.error('Error deleting old invitation:', deleteError);
          return { success: false, error: 'Error deleting old invitation: ' + deleteError.message };
        }
      }
    }

    // Utwórz nowe zaproszenie
    const { data, error } = await supabase
      .from('friend_invitations')
      .insert({
        sender_id: user.id,
        receiver_id: receiverId,
        status: 'pending'
      })
      .select()
      .single();

    if (error) {
      console.error('Error sending invitation:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error sending invitation:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Akceptuje zaproszenie do znajomych
 * @param {string} invitationId - ID zaproszenia
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function acceptFriendInvitation(invitationId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    // Sprawdź czy zaproszenie istnieje i czy użytkownik jest odbiorcą
    const { data: invitation, error: fetchError } = await supabase
      .from('friend_invitations')
      .select('*')
      .eq('id', invitationId)
      .eq('receiver_id', user.id)
      .eq('status', 'pending')
      .single();

    if (fetchError || !invitation) {
      return { success: false, error: 'Invitation not found or already processed' };
    }

    // Aktualizuj status zaproszenia
    const { data, error } = await supabase
      .from('friend_invitations')
      .update({ status: 'accepted' })
      .eq('id', invitationId)
      .select()
      .single();

    if (error) {
      console.error('Error accepting invitation:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error accepting invitation:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Odrzuca zaproszenie do znajomych
 * @param {string} invitationId - ID zaproszenia
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function rejectFriendInvitation(invitationId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    // Sprawdź czy zaproszenie istnieje i czy użytkownik jest odbiorcą
    const { data: invitation, error: fetchError } = await supabase
      .from('friend_invitations')
      .select('*')
      .eq('id', invitationId)
      .eq('receiver_id', user.id)
      .eq('status', 'pending')
      .single();

    if (fetchError || !invitation) {
      return { success: false, error: 'Invitation not found or already processed' };
    }

    // Aktualizuj status zaproszenia
    const { data, error } = await supabase
      .from('friend_invitations')
      .update({ status: 'rejected' })
      .eq('id', invitationId)
      .select()
      .single();

    if (error) {
      console.error('Error rejecting invitation:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error rejecting invitation:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Anuluje wysłane zaproszenie
 * @param {string} invitationId - ID zaproszenia
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function cancelFriendInvitation(invitationId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    // Sprawdź czy zaproszenie istnieje i czy użytkownik jest nadawcą
    const { data: invitation, error: fetchError } = await supabase
      .from('friend_invitations')
      .select('*')
      .eq('id', invitationId)
      .eq('sender_id', user.id)
      .eq('status', 'pending')
      .single();

    if (fetchError || !invitation) {
      return { success: false, error: 'Invitation not found or already processed' };
    }

    // Aktualizuj status zaproszenia
    const { data, error } = await supabase
      .from('friend_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId)
      .select()
      .single();

    if (error) {
      console.error('Error cancelling invitation:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa znajomego (usuwa zaproszenie o statusie accepted)
 * @param {string} friendId - ID znajomego do usunięcia
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function removeFriend(friendId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    // Znajdź zaproszenie o statusie accepted - użyj dwóch osobnych zapytań lub prostszej składni
    // Sprawdź oba kierunki osobno
    const { data: invitation1, error: error1 } = await supabase
      .from('friend_invitations')
      .select('*')
      .eq('sender_id', user.id)
      .eq('receiver_id', friendId)
      .eq('status', 'accepted')
      .maybeSingle();

    if (error1) {
      console.error('Error fetching invitation (direction 1):', error1);
    }

    // Jeśli nie znaleziono w pierwszym kierunku, sprawdź drugi
    let invitation = invitation1;
    if (!invitation) {
      const { data: invitation2, error: error2 } = await supabase
        .from('friend_invitations')
        .select('*')
        .eq('sender_id', friendId)
        .eq('receiver_id', user.id)
        .eq('status', 'accepted')
        .maybeSingle();

      if (error2) {
        console.error('Error fetching invitation (direction 2):', error2);
      }
      invitation = invitation2;
    }

    if (!invitation) {
      console.error('Friendship not found between', user.id, 'and', friendId);
      return { success: false, error: 'Friendship not found' };
    }

    console.log('Removing friendship invitation:', invitation.id);

    // Usuń zaproszenie (przyjaźń)
    const { error: deleteError, data: deleteData } = await supabase
      .from('friend_invitations')
      .delete()
      .eq('id', invitation.id)
      .select();

    if (deleteError) {
      console.error('Error removing friend:', deleteError);
      console.error('Delete error details:', {
        code: deleteError.code,
        message: deleteError.message,
        details: deleteError.details,
        hint: deleteError.hint
      });
      return { success: false, error: deleteError.message || deleteError.code || 'Unknown error' };
    }

    console.log('Friendship removed successfully:', deleteData);
    return { success: true, data: deleteData };
  } catch (error) {
    console.error('Error removing friend:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera listę znajomych użytkownika
 * @param {string} userId - ID użytkownika
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getFriends(userId) {
  try {
    const { data, error } = await supabase
      .from('friend_invitations')
      .select(`
        id,
        sender_id,
        receiver_id,
        status,
        created_at,
        sender:users!friend_invitations_sender_id_fkey(id, name, nickname, first_name, last_name, avatar_url, city, is_active, last_seen_at, hide_last_activity),
        receiver:users!friend_invitations_receiver_id_fkey(id, name, nickname, first_name, last_name, avatar_url, city, is_active, last_seen_at, hide_last_activity)
      `)
      .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
      .eq('status', 'accepted');

    if (error) {
      console.error('Error fetching friends:', error);
      return { success: false, error: error.message };
    }

    // Przetwórz dane, aby zwrócić informacje o znajomych (nie o użytkowniku)
    const friends = data.map(invitation => {
      const friend = invitation.sender_id === userId ? invitation.receiver : invitation.sender;
      return {
        id: friend.id,
        name: friend.name,
        nickname: friend.nickname,
        first_name: friend.first_name,
        last_name: friend.last_name,
        avatar_url: friend.avatar_url,
        city: friend.city,
        is_active: friend.is_active,
        last_seen_at: friend.last_seen_at,
        hide_last_activity: friend.hide_last_activity,
        friendship_created_at: invitation.created_at
      };
    });

    return { success: true, data: friends };
  } catch (error) {
    console.error('Error fetching friends:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wspólnych znajomych między dwoma użytkownikami
 * Używa funkcji SQL z SECURITY DEFINER żeby ominąć RLS
 * @param {string} userId1 - ID pierwszego użytkownika
 * @param {string} userId2 - ID drugiego użytkownika
 * @returns {Promise<{success: boolean, data?: Array, count?: number, error?: string}>}
 */
export async function getMutualFriends(userId1, userId2) {
  try {
    if (!userId1 || !userId2 || userId1 === userId2) {
      return { success: true, data: [], count: 0 };
    }

    // Użyj funkcji RPC która omija RLS
    const { data, error } = await supabase
      .rpc('get_mutual_friends', { 
        user1_id: userId1, 
        user2_id: userId2 
      });

    if (error) {
      console.error('Error fetching mutual friends via RPC:', error);
      return { success: false, error: error.message };
    }

    return { 
      success: true, 
      data: data || [], 
      count: data?.length || 0 
    };
  } catch (error) {
    console.error('Error fetching mutual friends:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera liczbę znajomych użytkownika
 * Używa funkcji SQL z SECURITY DEFINER żeby ominąć RLS
 * @param {string} userId - ID użytkownika
 * @returns {Promise<{success: boolean, count?: number, error?: string}>}
 */
export async function getFriendsCount(userId) {
  try {
    // Użyj funkcji RPC która omija RLS
    const { data, error } = await supabase
      .rpc('get_friends_count', { target_user_id: userId });

    if (error) {
      console.error('Error fetching friends count via RPC:', error);
      return { success: false, error: error.message };
    }

    return { success: true, count: data || 0 };
  } catch (error) {
    console.error('Error fetching friends count:', error);
    return { success: false, error: error.message };
  }
}
